import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Users,
  FileText,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Eye,
  Loader2,
  TrendingUp,
  Wallet,
  UserCheck,
  UserX,
  Trash2,
  AlertTriangle,
  Gift,
  Plus,
} from "lucide-react";
import { Textarea } from "../../components/ui/textarea";

const AdminReferrals = () => {
  const { token } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("applications");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Data
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [partners, setPartners] = useState([]);
  const [payouts, setPayouts] = useState([]);
  
  // Dialogs
  const [viewApplication, setViewApplication] = useState(null);
  const [processingAction, setProcessingAction] = useState(null);
  const [viewPayout, setViewPayout] = useState(null);
  const [completingPayout, setCompletingPayout] = useState(false);
  const [payoutNotes, setPayoutNotes] = useState("");
  const [deletePartnerDialog, setDeletePartnerDialog] = useState(null);
  const [deletingPartner, setDeletingPartner] = useState(false);
  const [clearDataDialog, setClearDataDialog] = useState(false);
  const [clearingData, setClearingData] = useState(false);
  const [addCreditDialog, setAddCreditDialog] = useState(null);
  const [addingCredit, setAddingCredit] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditReason, setCreditReason] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, appsRes, partnersRes, payoutsRes] = await Promise.all([
        fetch(`${API}/api/referrals/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/api/referrals/admin/applications`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/api/referrals/admin/partners`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/api/referrals/admin/payouts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (appsRes.ok) setApplications(await appsRes.json());
      if (partnersRes.ok) setPartners(await partnersRes.json());
      if (payoutsRes.ok) setPayouts(await payoutsRes.json());
    } catch (error) {
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveApplication = async (applicationId) => {
    setProcessingAction(applicationId);
    try {
      const response = await fetch(
        `${API}/api/referrals/admin/applications/${applicationId}/approve`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to approve application");
      }

      toast.success(`Application approved! Referral code: ${data.referral_code}`);
      setViewApplication(null);
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to approve application");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleDenyApplication = async (applicationId) => {
    setProcessingAction(applicationId);
    try {
      const response = await fetch(
        `${API}/api/referrals/admin/applications/${applicationId}/deny`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to deny application");
      }

      toast.success("Application denied");
      setViewApplication(null);
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to deny application");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleCompletePayout = async () => {
    if (!viewPayout) return;
    
    setCompletingPayout(true);
    try {
      const params = new URLSearchParams();
      if (payoutNotes) params.append("notes", payoutNotes);

      const response = await fetch(
        `${API}/api/referrals/admin/payouts/${viewPayout.payout_id}/complete?${params.toString()}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to complete payout");
      }

      toast.success("Payout marked as completed");
      setViewPayout(null);
      setPayoutNotes("");
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to complete payout");
    } finally {
      setCompletingPayout(false);
    }
  };

  const handleDeletePartner = async () => {
    if (!deletePartnerDialog) return;
    
    setDeletingPartner(true);
    try {
      const response = await fetch(
        `${API}/api/referrals/admin/partners/${deletePartnerDialog.partner_id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to delete partner");
      }

      toast.success(`Partner ${deletePartnerDialog.name} deleted successfully`);
      setDeletePartnerDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to delete partner");
    } finally {
      setDeletingPartner(false);
    }
  };

  const handleClearTestData = async () => {
    setClearingData(true);
    try {
      const response = await fetch(
        `${API}/api/referrals/admin/clear-test-data`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to clear test data");
      }

      const data = await response.json();
      toast.success(`Test data cleared: ${data.deleted.applications} applications, ${data.deleted.partners} partners, ${data.deleted.signups} signups, ${data.deleted.payouts} payouts`);
      setClearDataDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to clear test data");
    } finally {
      setClearingData(false);
    }
  };

  const handleAddCredit = async () => {
    if (!addCreditDialog) return;
    
    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount greater than 0");
      return;
    }
    
    if (!creditReason.trim() || creditReason.trim().length < 3) {
      toast.error("Please provide a reason for the credit");
      return;
    }
    
    setAddingCredit(true);
    try {
      const params = new URLSearchParams();
      params.append("amount", amount.toString());
      params.append("reason", creditReason.trim());

      const response = await fetch(
        `${API}/api/referrals/admin/partners/${addCreditDialog.partner_id}/add-credit?${params.toString()}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to add credit");
      }

      const data = await response.json();
      toast.success(data.message);
      setAddCreditDialog(null);
      setCreditAmount("");
      setCreditReason("");
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to add credit");
    } finally {
      setAddingCredit(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" />
            Approved
          </span>
        );
      case "denied":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
            <XCircle className="h-3 w-3" />
            Denied
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">
            <Clock className="h-3 w-3" />
            Pending
          </span>
        );
      case "completed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" />
            Completed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
            {status}
          </span>
        );
    }
  };

  const filteredApplications = applications.filter(
    (app) =>
      app.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPartners = partners.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.referral_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPayouts = payouts.filter(
    (p) =>
      p.partner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.partner_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingApplications = applications.filter((a) => a.status === "pending");
  const pendingPayouts = payouts.filter((p) => p.status === "pending");

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-rnb-blue" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-rnb-navy" data-testid="referrals-title">
              Referral Program
            </h1>
            <p className="text-rnb-gray">Manage referral partners and applications</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setClearDataDialog(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            data-testid="clear-test-data-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All Test Data
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-modern" data-testid="stat-active-partners">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-rnb-blue/10">
                  <Users className="h-5 w-5 text-rnb-blue" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-rnb-navy">{stats?.active_partners || 0}</p>
                  <p className="text-sm text-rnb-gray">Active Partners</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-modern" data-testid="stat-pending-apps">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100">
                  <FileText className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-rnb-navy">{stats?.pending_applications || 0}</p>
                  <p className="text-sm text-rnb-gray">Pending Applications</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-modern" data-testid="stat-total-referrals">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-rnb-navy">{stats?.confirmed_referrals || 0}</p>
                  <p className="text-sm text-rnb-gray">Total Referrals</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-modern" data-testid="stat-total-paid">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-rnb-teal/10">
                  <Wallet className="h-5 w-5 text-rnb-teal" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-rnb-navy">
                    ${(stats?.total_paid_out || 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-rnb-gray">Total Paid Out</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {(pendingApplications.length > 0 || pendingPayouts.length > 0) && (
          <div className="flex flex-wrap gap-3">
            {pendingApplications.length > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Clock className="h-4 w-4 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  {pendingApplications.length} application{pendingApplications.length !== 1 ? "s" : ""} pending review
                </span>
              </div>
            )}
            {pendingPayouts.length > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <DollarSign className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  {pendingPayouts.length} payout{pendingPayouts.length !== 1 ? "s" : ""} pending
                </span>
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-rnb-gray" />
          <Input
            placeholder="Search by name, email, or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="referrals-search"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="applications" className="gap-2" data-testid="tab-applications">
              <FileText className="h-4 w-4" />
              Applications
              {pendingApplications.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-yellow-500 text-white rounded-full">
                  {pendingApplications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="partners" className="gap-2" data-testid="tab-partners">
              <Users className="h-4 w-4" />
              Partners
            </TabsTrigger>
            <TabsTrigger value="payouts" className="gap-2" data-testid="tab-payouts">
              <DollarSign className="h-4 w-4" />
              Payouts
              {pendingPayouts.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                  {pendingPayouts.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Card className="card-modern">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Applicant</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredApplications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-rnb-gray">
                          No applications found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredApplications.map((app) => (
                        <TableRow key={app.application_id} data-testid={`application-row-${app.application_id}`}>
                          <TableCell className="font-medium">{app.name}</TableCell>
                          <TableCell>{app.email}</TableCell>
                          <TableCell>{formatDate(app.created_at)}</TableCell>
                          <TableCell>{getStatusBadge(app.status)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setViewApplication(app)}
                              data-testid={`view-application-${app.application_id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <Card className="card-modern">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Partner</TableHead>
                      <TableHead>Referral Code</TableHead>
                      <TableHead>Referrals</TableHead>
                      <TableHead>Earned</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPartners.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-rnb-gray">
                          No partners found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPartners.map((partner) => (
                        <TableRow key={partner.partner_id} data-testid={`partner-row-${partner.partner_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{partner.name}</p>
                              <p className="text-sm text-rnb-gray">{partner.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="px-2 py-1 bg-rnb-light rounded text-sm font-mono">
                              {partner.referral_code}
                            </code>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{partner.stats?.confirmed_referrals || 0}</span>
                            <span className="text-rnb-gray text-sm"> / {partner.stats?.total_referrals || 0}</span>
                          </TableCell>
                          <TableCell className="font-medium text-green-600">
                            ${(partner.stats?.total_earned || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-medium">
                            ${(partner.stats?.balance || 0).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {partner.status === "active" ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                                <CheckCircle className="h-3 w-3" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                                Inactive
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setAddCreditDialog(partner)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`add-credit-${partner.partner_id}`}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Credit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDeletePartnerDialog(partner)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-partner-${partner.partner_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <Card className="card-modern">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Partner</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayouts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-rnb-gray">
                          No payouts found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPayouts.map((payout) => (
                        <TableRow key={payout.payout_id} data-testid={`payout-row-${payout.payout_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{payout.partner_name}</p>
                              <p className="text-sm text-rnb-gray">{payout.partner_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="font-bold text-lg">
                            ${payout.amount?.toFixed(2)}
                          </TableCell>
                          <TableCell>{formatDate(payout.created_at)}</TableCell>
                          <TableCell>{getStatusBadge(payout.status)}</TableCell>
                          <TableCell className="text-right">
                            {payout.status === "pending" && (
                              <Button
                                size="sm"
                                onClick={() => setViewPayout(payout)}
                                className="btn-primary"
                                data-testid={`process-payout-${payout.payout_id}`}
                              >
                                Process
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* View Application Dialog */}
      <Dialog open={!!viewApplication} onOpenChange={() => setViewApplication(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Referral Application</DialogTitle>
            <DialogDescription>
              Review the application details below
            </DialogDescription>
          </DialogHeader>
          
          {viewApplication && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-rnb-gray">Name</p>
                  <p className="font-medium">{viewApplication.name}</p>
                </div>
                <div>
                  <p className="text-sm text-rnb-gray">Email</p>
                  <p className="font-medium">{viewApplication.email}</p>
                </div>
                {viewApplication.phone && (
                  <div>
                    <p className="text-sm text-rnb-gray">Phone</p>
                    <p className="font-medium">{viewApplication.phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-rnb-gray">Submitted</p>
                  <p className="font-medium">{formatDate(viewApplication.created_at)}</p>
                </div>
              </div>

              {viewApplication.social_media && Object.values(viewApplication.social_media).some(v => v) && (
                <div>
                  <p className="text-sm text-rnb-gray mb-2">Social Media</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(viewApplication.social_media).map(([platform, handle]) => (
                      handle && (
                        <span key={platform} className="px-3 py-1 bg-rnb-light rounded-full text-sm">
                          {platform}: {handle}
                        </span>
                      )
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm text-rnb-gray mb-1">Why interested</p>
                <p className="p-3 bg-rnb-light rounded-lg text-sm">{viewApplication.why_interested || "Not provided"}</p>
              </div>

              <div>
                <p className="text-sm text-rnb-gray mb-1">Referral strategy</p>
                <p className="p-3 bg-rnb-light rounded-lg text-sm">{viewApplication.referral_strategy || "Not provided"}</p>
              </div>

              <div className="flex items-center justify-between p-4 bg-rnb-light rounded-lg">
                <span className="font-medium">Status</span>
                {getStatusBadge(viewApplication.status)}
              </div>
            </div>
          )}

          {viewApplication?.status === "pending" && (
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => handleDenyApplication(viewApplication.application_id)}
                disabled={processingAction === viewApplication.application_id}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="deny-application-button"
              >
                {processingAction === viewApplication.application_id ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <UserX className="h-4 w-4 mr-2" />
                )}
                Deny
              </Button>
              <Button
                onClick={() => handleApproveApplication(viewApplication.application_id)}
                disabled={processingAction === viewApplication.application_id}
                className="btn-primary"
                data-testid="approve-application-button"
              >
                {processingAction === viewApplication.application_id ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <UserCheck className="h-4 w-4 mr-2" />
                )}
                Approve
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Process Payout Dialog */}
      <Dialog open={!!viewPayout} onOpenChange={() => { setViewPayout(null); setPayoutNotes(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Payout</DialogTitle>
            <DialogDescription>
              Mark this payout as completed after sending funds
            </DialogDescription>
          </DialogHeader>
          
          {viewPayout && (
            <div className="space-y-4">
              <div className="p-4 bg-rnb-light rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-rnb-gray">Partner</span>
                  <span className="font-medium">{viewPayout.partner_name}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-rnb-gray">Email</span>
                  <span className="font-medium">{viewPayout.partner_email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-rnb-gray">Amount</span>
                  <span className="text-2xl font-bold text-green-600">${viewPayout.amount?.toFixed(2)}</span>
                </div>
              </div>

              {viewPayout.payout_method && (
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-rnb-gray mb-1">Payout Method</p>
                  <p className="font-medium capitalize">{viewPayout.payout_method.type}</p>
                  <p className="text-sm text-rnb-gray">{viewPayout.payout_method.account_info}</p>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm text-rnb-gray">Notes (optional)</label>
                <Input
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  placeholder="Transaction ID, confirmation number, etc."
                  data-testid="payout-notes-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setViewPayout(null); setPayoutNotes(""); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCompletePayout}
              disabled={completingPayout}
              className="btn-primary"
              data-testid="complete-payout-button"
            >
              {completingPayout ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Completed
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Partner Confirmation Dialog */}
      <Dialog open={!!deletePartnerDialog} onOpenChange={() => setDeletePartnerDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Referral Partner
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {deletePartnerDialog && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 mb-2">
                  Are you sure you want to delete this partner? This will permanently remove:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Partner account: <strong>{deletePartnerDialog.name}</strong></li>
                  <li>All referral signups attributed to them</li>
                  <li>All payout history</li>
                  <li>All milestone bonuses earned</li>
                </ul>
              </div>
              
              <div className="p-3 bg-rnb-light rounded-lg">
                <p className="text-sm text-rnb-gray">Partner Details</p>
                <p className="font-medium">{deletePartnerDialog.name}</p>
                <p className="text-sm text-rnb-gray">{deletePartnerDialog.email}</p>
                <p className="text-sm font-mono mt-1">Code: {deletePartnerDialog.referral_code}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePartnerDialog(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeletePartner}
              disabled={deletingPartner}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-partner"
            >
              {deletingPartner ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Partner
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear All Test Data Dialog */}
      <Dialog open={clearDataDialog} onOpenChange={setClearDataDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Clear All Referral Test Data
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 mb-2">
                Are you sure you want to clear ALL referral program data? This will permanently delete:
              </p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                <li>All referral applications ({applications.length} total)</li>
                <li>All referral partners ({partners.length} total)</li>
                <li>All referral signups and commissions</li>
                <li>All payout requests and history</li>
                <li>All milestone bonuses</li>
              </ul>
            </div>
            
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> This is typically used to clear test data before going live. 
                Do not use this in production with real partners.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setClearDataDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleClearTestData}
              disabled={clearingData}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-clear-data"
            >
              {clearingData ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Credit Dialog */}
      <Dialog open={!!addCreditDialog} onOpenChange={() => { setAddCreditDialog(null); setCreditAmount(""); setCreditReason(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-green-600" />
              Add Manual Credit
            </DialogTitle>
            <DialogDescription>
              Add a bonus or one-time credit to this partner's account
            </DialogDescription>
          </DialogHeader>
          
          {addCreditDialog && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-rnb-gray">Partner</p>
                <p className="font-semibold text-rnb-navy">{addCreditDialog.name}</p>
                <p className="text-sm text-rnb-gray">{addCreditDialog.email}</p>
                <p className="text-sm font-mono mt-1">Code: {addCreditDialog.referral_code}</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-rnb-navy">Credit Amount ($)</label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="Enter amount (e.g., 100.00)"
                  data-testid="credit-amount-input"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-rnb-navy">Reason for Credit</label>
                <Textarea
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  placeholder="e.g., Referral contest winner, Loyalty bonus, Promotional credit..."
                  rows={3}
                  data-testid="credit-reason-input"
                />
                <p className="text-xs text-rnb-gray">
                  This will be visible to the partner and included in their notification email.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddCreditDialog(null); setCreditAmount(""); setCreditReason(""); }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddCredit}
              disabled={addingCredit || !creditAmount || !creditReason.trim()}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-add-credit"
            >
              {addingCredit ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add ${creditAmount || "0"} Credit
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminReferrals;
